// src/pages/admin/FpoApprovalsAdmin.js
import { useEffect, useMemo, useState, useCallback } from "react";
import { auth, db } from "../../firebaseConfig";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  serverTimestamp,
  // ✅ member_requests aggregation + delete
  collectionGroup,
  getDocs,
  deleteDoc,
} from "firebase/firestore";

const cx = (...xs) => xs.filter(Boolean).join(" ");

/* =========================
   CSV helpers
========================= */
const toCsvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const downloadCsv = ({ filename, header, rows }) => {
  const csv = [header.map(toCsvCell).join(","), ...rows.map((r) => r.map(toCsvCell).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function FpoApprovalsAdmin() {
  const [user, setUser] = useState(null);
  const [roleOk, setRoleOk] = useState(false);
  const [loadingRole, setLoadingRole] = useState(true);

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("pending"); // pending | approved

  const [selected, setSelected] = useState(null); // row for modal

  // org-wise farmer registrations (member_requests)
  const [orgReqMap, setOrgReqMap] = useState({}); // { [orgUid]: { count, approvedCount, pendingCount, list: [...] } }
  const [orgReqLoading, setOrgReqLoading] = useState(false);

  /* =========================
     Role check
  ========================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      if (!u) {
        window.location.href = "/admin/login";
        return;
      }

      setLoadingRole(true);
      try {
        const token = await u.getIdTokenResult(true);
        const claims = token?.claims || {};
        const claimAdmin = claims.admin === true;
        const claimRole = String(claims.role || "");
        let ok = claimAdmin || ["Admin", "CIO", "CEO"].includes(claimRole);

        // fallback: portal_users/{uid}.role
        if (!ok) {
          const pSnap = await getDoc(doc(db, "portal_users", u.uid));
          if (pSnap.exists()) {
            const r = String((pSnap.data() || {}).role || "");
            if (["Admin", "CIO", "CEO"].includes(r)) ok = true;
          }
        }

        setRoleOk(ok);
        if (!ok) {
          alert("Access denied: Admin/CIO/CEO required.");
          await signOut(auth);
          window.location.href = "/";
          return;
        }
      } catch (e) {
        console.error("Role check error:", e);
        alert("Role check failed.");
      } finally {
        setLoadingRole(false);
      }
    });

    return () => unsub();
  }, []);

  /* =========================
     Load farmer_vendors
  ========================= */
  useEffect(() => {
    if (!roleOk) return;

    const qRef = query(collection(db, "farmer_vendors"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(qRef, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      setRows(list);
    });

    return () => unsub();
  }, [roleOk]);

  /* =========================
     ✅ Fallback loader: per-org subcollection read
     (Works even if collectionGroup blocked by rules/index)
  ========================= */
  const loadOrgReqMapFallback = useCallback(
    async (orgIds = []) => {
      try {
        setOrgReqLoading(true);

        const map = {};
        const ids = Array.from(new Set((orgIds || []).filter(Boolean)));

        await Promise.all(
          ids.map(async (orgId) => {
            try {
              const snap = await getDocs(collection(db, "farmer_vendors", orgId, "member_requests"));
              if (snap.empty) return;

              if (!map[orgId]) {
                map[orgId] = { count: 0, approvedCount: 0, pendingCount: 0, list: [] };
              }

              snap.forEach((d) => {
                const data = d.data() || {};
                const st = String(data.status || "pending").toLowerCase();

                map[orgId].count += 1;
                if (st === "approved") map[orgId].approvedCount += 1;
                else map[orgId].pendingCount += 1;

                map[orgId].list.push({
                  id: d.id,
                  memberDocId: data.memberDocId || d.id,
                  status: data.status || "pending",
                  createdAt: data.createdAt || null,
                  from_member_name: data.from_member_name || "",
                  from_member_mobile: data.from_member_mobile || "",
                  from_member_email: data.from_member_email || "",
                  membership_type: data.membership_type || "",
                  farmer_category: data.farmer_category || "",
                  membership_fee: data.membership_fee || 0,
                  linked_org_uid: orgId,
                  linked_org_name: data.linked_org_name || "",
                });
              });
            } catch (e) {
              console.warn("fallback member_requests read failed for", orgId, e?.message || e);
            }
          })
        );

        // Merge: prefer non-empty collectionGroup data if it exists
        setOrgReqMap((prev) => {
          const out = { ...(prev || {}) };
          Object.keys(map).forEach((k) => {
            if (!out[k] || !(out[k]?.count > 0)) out[k] = map[k];
          });
          return out;
        });
      } finally {
        setOrgReqLoading(false);
      }
    },
    [setOrgReqMap]
  );

  /* =========================
     ✅ Primary: collectionGroup(member_requests)
     ✅ Fallback: per-org member_requests
  ========================= */
  useEffect(() => {
    if (!roleOk) return;

    setOrgReqLoading(true);

    const qReq = query(collectionGroup(db, "member_requests"), orderBy("createdAt", "desc"));

    const unsubReq = onSnapshot(
      qReq,
      async (snap) => {
        const map = {};

        snap.docs.forEach((d) => {
          const data = d.data() || {};
          const orgUid = String(data.linked_org_uid || "").trim();
          if (!orgUid) return;

          if (!map[orgUid]) {
            map[orgUid] = { count: 0, approvedCount: 0, pendingCount: 0, list: [] };
          }

          const st = String(data.status || "pending").toLowerCase();
          map[orgUid].count += 1;
          if (st === "approved") map[orgUid].approvedCount += 1;
          else map[orgUid].pendingCount += 1;

          map[orgUid].list.push({
            id: d.id,
            memberDocId: data.memberDocId || d.id,
            status: data.status || "pending",
            createdAt: data.createdAt || null,
            from_member_name: data.from_member_name || "",
            from_member_mobile: data.from_member_mobile || "",
            from_member_email: data.from_member_email || "",
            membership_type: data.membership_type || "",
            farmer_category: data.farmer_category || "",
            membership_fee: data.membership_fee || 0,
            linked_org_uid: data.linked_org_uid || "",
            linked_org_name: data.linked_org_name || "",
          });
        });

        setOrgReqMap(map);
        setOrgReqLoading(false);

        // If collectionGroup returns empty, fallback
        if (snap.empty) {
          const orgIds = (rows || []).map((r) => r.id).filter(Boolean);
          if (orgIds.length) await loadOrgReqMapFallback(orgIds);
        }
      },
      async (err) => {
        console.error("member_requests collectionGroup error:", err);
        setOrgReqLoading(false);
        const orgIds = (rows || []).map((r) => r.id).filter(Boolean);
        if (orgIds.length) await loadOrgReqMapFallback(orgIds);
      }
    );

    return () => unsubReq();
  }, [roleOk, rows, loadOrgReqMapFallback]);

  /* =========================
     Filters
  ========================= */
  const filtered = useMemo(() => {
    const q = String(search || "").toLowerCase().trim();
    const wantApproved = tab === "approved";

    return (rows || [])
      .filter((r) => {
        const st = String(r.status || "").toLowerCase();
        if (wantApproved) return st === "approved";
        return st === "pending" || st === "payment_pending" || st === "" || st === "submitted" || st === "rejected";
      })
      .filter((r) => {
        const cat = String(r.farmer_category || r.vendor_type || "").toLowerCase();
        const looksOrg =
          cat.includes("fpo") ||
          cat.includes("cooperative") ||
          cat.includes("society") ||
          String(r.role || "").toLowerCase() === "org";

        if (!looksOrg) return false;

        if (!q) return true;
        const name = String(r.name || r.org_name || r.company_name || "").toLowerCase();
        const email = String(r.email || r.auth?.email || "").toLowerCase();
        const mob = String(r.contact_mobile || r.profile?.contactPhone || "").toLowerCase();
        const reg = String(r.registration_no || r.regNo || r.reg_no || "").toLowerCase();
        return name.includes(q) || email.includes(q) || mob.includes(q) || reg.includes(q);
      });
  }, [rows, tab, search]);

  /* =========================
     Actions: approve / reject / delete
  ========================= */
  const approve = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Approve ${row.name || row.org_name || row.company_name || "this organization"} ?`)) return;

    await updateDoc(doc(db, "farmer_vendors", row.id), {
      status: "approved",
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    alert("✅ Approved successfully.");
  };

  const rejectOrg = async (row) => {
    if (!row?.id) return;
    const name = row.name || row.org_name || row.company_name || "this organization";
    if (!window.confirm(`Reject ${name} ?`)) return;

    await updateDoc(doc(db, "farmer_vendors", row.id), {
      status: "rejected",
      rejectedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    alert("⛔ Rejected successfully.");
  };

  const deleteOrg = async (row) => {
    if (!row?.id) return;
    const name = row.name || row.org_name || row.company_name || "this organization";
    if (!window.confirm(`DELETE ${name} ?\n\nThis will permanently remove the organization record.`)) return;

    await deleteDoc(doc(db, "farmer_vendors", row.id));
    alert("🗑️ Deleted successfully.");
    setSelected(null);
  };

  /* =========================
     ✅ Download farmers CSV (ALWAYS WORKS)
     - If orgReqMap empty, fetch directly from subcollection
  ========================= */
  const downloadOrgFarmersCsv = async (orgId, orgName = "organization") => {
    let meta = orgReqMap?.[orgId] || null;
    let list = Array.isArray(meta?.list) ? meta.list : [];

    if (!list.length) {
      try {
        const snap = await getDocs(collection(db, "farmer_vendors", orgId, "member_requests"));
        const tmp = [];
        snap.forEach((d) => {
          const data = d.data() || {};
          tmp.push({
            id: d.id,
            memberDocId: data.memberDocId || d.id,
            status: data.status || "pending",
            createdAt: data.createdAt || null,
            from_member_name: data.from_member_name || "",
            from_member_mobile: data.from_member_mobile || "",
            from_member_email: data.from_member_email || "",
            membership_type: data.membership_type || "",
            farmer_category: data.farmer_category || "",
            membership_fee: data.membership_fee || 0,
            linked_org_uid: orgId,
            linked_org_name: data.linked_org_name || "",
          });
        });
        list = tmp;
      } catch (e) {
        alert("No farmer list found / permission blocked for this org.");
        return;
      }
    }

    const header = [
      "MemberDocId",
      "Name",
      "Mobile",
      "Email",
      "Membership Type",
      "Farmer Category",
      "Fee",
      "Status",
      "Linked Org UID",
      "Linked Org Name",
    ];

    const rowsCsv = list.map((r) => [
      r.memberDocId || r.id || "",
      r.from_member_name || "",
      r.from_member_mobile || "",
      r.from_member_email || "",
      r.membership_type || "",
      r.farmer_category || "",
      r.membership_fee || 0,
      r.status || "",
      r.linked_org_uid || orgId,
      r.linked_org_name || "",
    ]);

    downloadCsv({
      filename: `FPO_Farmers_${String(orgName || "org").replace(/[^\w\-]+/g, "_")}.csv`,
      header,
      rows: rowsCsv,
    });
  };

  const logout = async () => {
    await signOut(auth);
    window.location.href = "/admin/login";
  };

  if (loadingRole) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-600">
        Checking admin access…
      </div>
    );
  }

  if (!roleOk) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">
              Admin Portal
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">
              FPO / Cooperative Approvals
            </h1>
            <p className="text-xs text-slate-600 mt-1">
              Pending registrations ko verify karke approve karein. Reject/Delete enabled.
            </p>

            <div className="text-[11px] text-slate-500 mt-1">
              Farmer registrations:{" "}
              {orgReqLoading ? <b>Loading…</b> : <b>{Object.keys(orgReqMap || {}).length}</b>}{" "}
              organization(s) mapped
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 font-semibold"
            >
              Refresh
            </button>
            <button
              onClick={logout}
              className="px-4 py-2 rounded-lg bg-rose-600 text-white font-semibold hover:bg-rose-700"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-4 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex gap-2">
            <button
              onClick={() => setTab("pending")}
              className={cx(
                "px-4 py-2 rounded-lg text-sm font-semibold border",
                tab === "pending"
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              )}
            >
              Pending
            </button>
            <button
              onClick={() => setTab("approved")}
              className={cx(
                "px-4 py-2 rounded-lg text-sm font-semibold border",
                tab === "approved"
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              )}
            >
              Approved
            </button>
          </div>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search org / email / mobile / reg no..."
            className="w-full md:w-96 border border-slate-300 rounded-lg p-2.5"
          />
        </div>

        {/* Table */}
        <div className="mt-4 bg-white rounded-2xl border border-black shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-black bg-slate-50 text-sm font-bold text-slate-900">
            Organizations ({filtered.length})
          </div>

          <div className="overflow-auto">
            <table className="min-w-full text-sm border-collapse">
              <thead className="bg-white border-b border-black">
                <tr className="text-left">
                  <th className="p-3 border-r border-black">Organization</th>
                  <th className="p-3 border-r border-black">Type</th>
                  <th className="p-3 border-r border-black">Contact</th>
                  <th className="p-3 border-r border-black">Email</th>
                  <th className="p-3 border-r border-black">Reg No</th>
                  <th className="p-3 border-r border-black">Farmers</th>
                  <th className="p-3 border-r border-black">Status</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((r) => {
                  const name = r.name || r.org_name || r.company_name || "-";
                  const type = r.farmer_category || r.vendor_type || "-";
                  const contactName = r.contact_name || r.profile?.contactName || "-";
                  const contactMobile = r.contact_mobile || r.profile?.contactPhone || "-";
                  const email = r.email || r.auth?.email || "-";
                  const regNo = r.registration_no || r.regNo || r.reg_no || "-";
                  const st = String(r.status || "pending").toUpperCase();

                  const meta = orgReqMap?.[r.id] || null;
                  const farmersCount = Number(meta?.count || 0);
                  const farmersApproved = Number(meta?.approvedCount || 0);

                  return (
                    <tr key={r.id} className="border-b border-black">
                      <td className="p-3 border-r border-black">
                        <div className="font-semibold text-slate-900">{name}</div>
                        <div className="text-[11px] text-slate-500">
                          {r.state || "-"}{r.district ? ` / ${r.district}` : ""}
                        </div>
                      </td>
                      <td className="p-3 border-r border-black">{type}</td>
                      <td className="p-3 border-r border-black">
                        <div className="font-semibold">{contactName}</div>
                        <div className="text-xs text-slate-600">{contactMobile}</div>
                      </td>
                      <td className="p-3 border-r border-black">{email}</td>
                      <td className="p-3 border-r border-black">{regNo}</td>
                      <td className="p-3 border-r border-black">
                        <div className="font-semibold text-slate-900">{farmersCount}</div>
                        <div className="text-[11px] text-slate-500">Approved: {farmersApproved}</div>
                      </td>
                      <td className="p-3 border-r border-black">
                        <span
                          className={cx(
                            "px-2 py-1 rounded-full text-xs font-bold",
                            st === "APPROVED"
                              ? "bg-emerald-50 text-emerald-700"
                              : st === "REJECTED"
                              ? "bg-rose-50 text-rose-700"
                              : "bg-amber-50 text-amber-700"
                          )}
                        >
                          {st}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setSelected(r)}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 font-semibold"
                          >
                            View
                          </button>

                          {String(r.status || "").toLowerCase() !== "approved" && (
                            <button
                              onClick={() => approve(r)}
                              className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                            >
                              Approve
                            </button>
                          )}

                          {String(r.status || "").toLowerCase() !== "rejected" && (
                            <button
                              onClick={() => rejectOrg(r)}
                              className="px-3 py-1.5 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700"
                            >
                              Reject
                            </button>
                          )}

                          <button
                            onClick={() => deleteOrg(r)}
                            className="px-3 py-1.5 rounded-lg bg-rose-600 text-white font-semibold hover:bg-rose-700"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {filtered.length === 0 && (
                  <tr>
                    <td className="p-5 text-slate-500" colSpan={8}>
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        {selected && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="w-full max-w-3xl bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <div className="font-extrabold text-slate-900">
                  {selected.name || selected.org_name || selected.company_name || "Organization"}
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 font-semibold"
                >
                  Close
                </button>
              </div>

              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="border border-slate-200 rounded-xl p-4">
                  <div className="text-xs text-slate-500">Type</div>
                  <div className="font-semibold">{selected.farmer_category || selected.vendor_type || "-"}</div>

                  <div className="mt-3 text-xs text-slate-500">Registration No</div>
                  <div className="font-semibold">{selected.registration_no || selected.regNo || selected.reg_no || "-"}</div>

                  <div className="mt-3 text-xs text-slate-500">Location</div>
                  <div className="font-semibold">
                    {(selected.state || "-") + (selected.district ? ` / ${selected.district}` : "")}
                  </div>

                  <div className="mt-3 text-xs text-slate-500">Address</div>
                  <div className="font-semibold">{selected.address || "-"}</div>

                  <div className="mt-4 border-t border-slate-200 pt-3">
                    <div className="text-xs text-slate-500">Farmers Registered</div>
                    <div className="font-extrabold text-slate-900">
                      {Number((orgReqMap?.[selected.id]?.count) || 0)}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      Approved: {Number((orgReqMap?.[selected.id]?.approvedCount) || 0)} • Pending:{" "}
                      {Number((orgReqMap?.[selected.id]?.pendingCount) || 0)}
                    </div>

                    <button
                      type="button"
                      onClick={async () =>
                        await downloadOrgFarmersCsv(
                          selected.id,
                          selected.name || selected.org_name || selected.company_name || "organization"
                        )
                      }
                      className="mt-3 px-4 py-2 rounded-lg bg-slate-900 text-white font-semibold hover:bg-slate-800"
                    >
                      ⬇ Download Farmers List (CSV)
                    </button>
                  </div>
                </div>

                <div className="border border-slate-200 rounded-xl p-4">
                  <div className="text-xs text-slate-500">Contact</div>
                  <div className="font-semibold">{selected.contact_name || "-"}</div>
                  <div className="text-slate-600">{selected.contact_mobile || "-"}</div>

                  <div className="mt-3 text-xs text-slate-500">Email</div>
                  <div className="font-semibold">{selected.email || selected.auth?.email || "-"}</div>

                  <div className="mt-4 font-bold text-slate-900">Documents</div>
                  <div className="mt-2 space-y-2">
                    <a
                      className="block text-emerald-700 font-semibold underline"
                      href={selected.documents?.registration_certificate_url || "#"}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Registration Certificate
                    </a>
                    {selected.documents?.pan_url ? (
                      <a
                        className="block text-emerald-700 font-semibold underline"
                        href={selected.documents?.pan_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        PAN
                      </a>
                    ) : (
                      <div className="text-xs text-slate-500">PAN: not uploaded</div>
                    )}
                    {selected.documents?.board_resolution_url ? (
                      <a
                        className="block text-emerald-700 font-semibold underline"
                        href={selected.documents?.board_resolution_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Board Resolution
                      </a>
                    ) : (
                      <div className="text-xs text-slate-500">Resolution: not uploaded</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="px-5 py-4 border-t border-slate-200 bg-white flex items-center justify-end gap-2">
                {String(selected.status || "").toLowerCase() !== "approved" && (
                  <button
                    onClick={() => approve(selected)}
                    className="px-5 py-2 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                  >
                    Approve Organization
                  </button>
                )}
                {String(selected.status || "").toLowerCase() !== "rejected" && (
                  <button
                    onClick={() => rejectOrg(selected)}
                    className="px-5 py-2 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700"
                  >
                    Reject Organization
                  </button>
                )}
                <button
                  onClick={() => deleteOrg(selected)}
                  className="px-5 py-2 rounded-lg bg-rose-600 text-white font-semibold hover:bg-rose-700"
                >
                  Delete Organization
                </button>
                <button
                  onClick={() => setSelected(null)}
                  className="px-5 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="text-[11px] text-slate-500 mt-3">
          Note: Reject/Delete enabled for admin use.
        </div>
      </div>
    </div>
  );
}
